import { useEffect, useState, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Page,
  Header,
  Content,
  Progress,
  ResponseErrorPanel,
  Link,
  HeaderLabel,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Box,
  SvgIcon,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import GroupIcon from '@material-ui/icons/People';
import FolderIcon from '@material-ui/icons/FolderOpen';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const GitLabIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 380 380">
    <path
      fill="currentColor"
      d="M282.83 170.73l-.27-.69-26.14-68.22a6.81 6.81 0 00-2.69-3.24 7 7 0 00-8 .43 7 7 0 00-2.32 3.52l-17.65 54h-69.57l-17.65-54a6.86 6.86 0 00-2.32-3.53 7 7 0 00-8-.43 6.87 6.87 0 00-2.69 3.24L99.44 170l-.26.69a48.54 48.54 0 0016.1 56.1l.09.07.24.17 39.82 29.82 19.7 14.91 12 9.06a8.07 8.07 0 009.76 0l12-9.06 19.7-14.91 40.06-30 .1-.08a48.56 48.56 0 0016.08-56.04z"
    />
  </SvgIcon>
);

interface OrgTeamNode {
  entity: Entity;
  name: string;
  title: string;
  children: OrgTeamNode[];
}

const useStyles = makeStyles(theme => ({
  treeRoot: {
    width: '100%',
    maxWidth: 900,
  },
  row: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    padding: theme.spacing(0.5, 1),
    cursor: 'pointer',
  },
  expandButton: {
    padding: 4,
    marginRight: theme.spacing(0.5),
  },
  expandPlaceholder: {
    width: 28,
    marginRight: theme.spacing(0.5),
    flexShrink: 0,
  },
  folderIcon: {
    minWidth: 'unset',
    marginRight: theme.spacing(1),
    color: theme.palette.type === 'dark' ? '#90caf9' : '#1976d2',
  },
  leafIcon: {
    minWidth: 'unset',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  groupLink: {
    fontWeight: 500,
    fontSize: '0.9rem',
  },
  childCount: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginLeft: theme.spacing(1),
  },
  billingNames: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
}));

function getOrgTeamChildren(entity: Entity): string[] {
  const ann = entity.metadata.annotations?.['geniussports.com/org-team-children'];
  if (!ann) return [];
  return ann.split(',').map(s => s.trim()).filter(Boolean);
}

function getOrgTeamBillingNames(entity: Entity): string[] {
  const ann = entity.metadata.annotations?.['geniussports.com/aws-billing-team-names'];
  if (!ann) return [];
  return ann.split(',').map(s => s.trim()).filter(Boolean);
}

function buildTree(entities: Entity[]): OrgTeamNode[] {
  const byName = new Map<string, Entity>();
  for (const e of entities) {
    byName.set(e.metadata.name, e);
  }

  const childNames = new Set<string>();
  for (const e of entities) {
    for (const c of getOrgTeamChildren(e)) {
      childNames.add(c);
    }
  }

  function toNode(entity: Entity): OrgTeamNode {
    const children: OrgTeamNode[] = getOrgTeamChildren(entity)
      .map(name => byName.get(name))
      .filter((e): e is Entity => !!e)
      .map(toNode)
      .sort((a, b) => a.title.localeCompare(b.title));

    return {
      entity,
      name: entity.metadata.name,
      title:
        (entity.metadata as any).title ??
        (entity as any).spec?.profile?.displayName ??
        entity.metadata.name,
      children,
    };
  }

  const roots = entities
    .filter(e => !childNames.has(e.metadata.name))
    .map(toNode)
    .sort((a, b) => a.title.localeCompare(b.title));

  return roots;
}

function TreeNode({ node, depth }: { node: OrgTeamNode; depth: number }) {
  const classes = useStyles();
  const [open, setOpen] = useState(depth === 0 && node.children.length > 0);

  const hasChildren = node.children.length > 0;
  const namespace = node.entity.metadata.namespace ?? 'default';
  const billingNames = getOrgTeamBillingNames(node.entity);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(prev => !prev);
  }, []);

  return (
    <>
      <ListItem
        className={classes.row}
        style={{ paddingLeft: 16 + depth * 28 }}
        onClick={hasChildren ? handleToggle : undefined}
        component="div"
      >
        {hasChildren ? (
          <IconButton
            className={classes.expandButton}
            size="small"
            onClick={handleToggle}
            aria-label={open ? 'collapse' : 'expand'}
          >
            {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        ) : (
          <span className={classes.expandPlaceholder} />
        )}
        <ListItemIcon
          className={hasChildren ? classes.folderIcon : classes.leafIcon}
        >
          {hasChildren ? <FolderIcon /> : <GroupIcon />}
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Box display="flex" alignItems="center">
              <Link
                to={`/catalog/${namespace}/group/${node.name}`}
                className={classes.groupLink}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                {node.title}
              </Link>
              {hasChildren && (
                <Typography className={classes.childCount}>
                  ({node.children.length})
                </Typography>
              )}
            </Box>
          }
          secondary={
            billingNames.length > 0 ? (
              <Typography className={classes.billingNames}>
                {billingNames.join(', ')}
              </Typography>
            ) : undefined
          }
        />
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {node.children.map(child => (
              <TreeNode key={child.name} node={child} depth={depth + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export const OrgTeamsPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    catalogApi
      .getEntities({
        filter: {
          kind: 'Group',
          'metadata.annotations.geniussports.com/org-team': 'true',
        },
      })
      .then(({ items }) => {
        if (active) setEntities(items);
      })
      .catch(e => {
        if (active) setError(e);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [catalogApi]);

  const tree = buildTree(entities);

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Organization Teams" />
        <Content>
          <ResponseErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title="Organization Teams"
        subtitle="This is a static page that displays a team hierarchy across the organization.  You can edit at the gitlab link on the right.  Teams should be structured in a way that they roll up in a useful way to a group/org."
      >
        <HeaderLabel label="Total" value={String(entities.length)} />
        <HeaderLabel label="Top-level" value={String(tree.length)} />
        <Tooltip title="View source in GitLab">
          <IconButton
            component="a"
            href="https://gitlab.com/geniussports/core/infra-platform/backstage-components/-/tree/main/static?ref_type=heads"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit' }}
            size="small"
          >
            <GitLabIcon />
          </IconButton>
        </Tooltip>
      </Header>
      <Content>
        {loading ? (
          <Progress />
        ) : tree.length === 0 ? (
          <Typography>
            No org-team groups found. Ensure the org-team enrichment
            processor is running and YAML files are configured.
          </Typography>
        ) : (
          <List disablePadding className={classes.treeRoot}>
            {tree.map(node => (
              <TreeNode key={node.name} node={node} depth={0} />
            ))}
          </List>
        )}
      </Content>
    </Page>
  );
};
